import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, Phone, Mail, MapPin, Check, Plus, Minus, AlertCircle } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCMS } from '../context/CMSContext';
import { contactPage, faqs as defaultFaqs } from '../data/mockData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FIELD_LABEL = 'text-[11px] uppercase tracking-[0.25em] text-white/45 font-bold';
const FIELD_INPUT = 'w-full bg-transparent border-0 border-b border-white/15 text-white placeholder-white/30 py-3.5 px-0 text-base focus:border-white focus:outline-none transition-colors';
const FIELD_SELECT = 'w-full bg-[#0B1221] border border-white/10 text-white py-3 px-4 rounded-lg text-sm focus:border-white focus:outline-none transition-colors appearance-none';

const initial = { name: '', email: '', phone: '', projectType: '', budget: '', timeline: '', message: '' };

const FAQRow = ({ faq, isOpen, onToggle }) => (
  <div className="border-t border-white/8">
    <button onClick={onToggle} className="w-full flex items-center justify-between py-5 text-left" data-testid={`contact-faq-${faq.id}`}>
      <span className="font-display text-base md:text-lg text-white/90">{faq.q}</span>
      {isOpen ? <Minus className="w-4 h-4 text-white/50" /> : <Plus className="w-4 h-4 text-white/50" />}
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }} className="overflow-hidden">
          <p className="pb-5 text-white/60 text-sm font-light leading-relaxed">{faq.a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const ContactPage = () => {
  const { get } = useCMS();
  const company = get('company_info') || {};
  const faqs = get('faqs') || defaultFaqs;
  const [data, setData] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [globalErr, setGlobalErr] = useState('');

  const update = (k) => (e) => {
    setData({ ...data, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: null });
  };

  const validate = () => {
    const e = {};
    if (!data.name.trim()) e.name = 'Please enter your name.';
    if (!data.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) e.email = 'Please enter a valid email.';
    if (!data.phone.trim()) e.phone = 'Please enter a phone number.';
    if (!data.message.trim() || data.message.trim().length < 10) e.message = 'Please share a few sentences about your project.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setGlobalErr('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      const combinedMessage = [
        data.projectType && `Project type: ${data.projectType}`,
        data.budget && `Budget: ${data.budget}`,
        data.timeline && `Timeline: ${data.timeline}`,
        '',
        data.message,
      ].filter(Boolean).join('\n');
      await axios.post(`${API}/quote-requests`, {
        name: data.name, email: data.email, phone: data.phone, message: combinedMessage,
      });
      setSubmitted(true);
      setData(initial);
    } catch {
      setGlobalErr('Something went wrong. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050914]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 pb-14 lg:pt-44 lg:pb-20 overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 hero-mesh" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <p className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-white/50 font-bold mb-6">
            <span className="w-8 h-px bg-white/30" /> Contact
          </p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter text-white leading-[1.02] max-w-4xl">
            Tell us what you&rsquo;re building.
          </h1>
          <p className="mt-7 text-white/60 text-lg max-w-2xl font-light leading-relaxed">
            Send us a few sentences about the project and we&rsquo;ll come back with a clear scope, a fair quote and a timeline you can plan around.
          </p>
        </div>
      </section>

      {/* Form + details */}
      <section className="relative pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14">
          <aside className="lg:col-span-4 space-y-8">
            <div className="p-6 rounded-2xl border border-white/10 bg-[#0B1221]/80">
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-bold mb-4">Direct</p>
              {company.phone && (
                <a href={`tel:${company.phone}`} className="flex items-center gap-3 group mb-4">
                  <div className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center group-hover:border-white transition-colors"><Phone className="w-4 h-4 text-white/70 group-hover:text-white transition-colors" /></div>
                  <div><p className="text-white text-sm font-medium">{company.phone}</p><p className="text-xs text-white/40">Mon–Fri 9:00–18:00</p></div>
                </a>
              )}
              {company.email && (
                <a href={`mailto:${company.email}`} className="flex items-center gap-3 group mb-4">
                  <div className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center group-hover:border-white transition-colors"><Mail className="w-4 h-4 text-white/70 group-hover:text-white transition-colors" /></div>
                  <div><p className="text-white text-sm font-medium">{company.email}</p><p className="text-xs text-white/40">Response within 1 business day</p></div>
                </a>
              )}
              {company.address && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center"><MapPin className="w-4 h-4 text-white/70" /></div>
                  <div><p className="text-white text-sm font-medium">{company.address}</p><p className="text-xs text-white/40">Based in Canada · working globally</p></div>
                </div>
              )}
            </div>
            <div className="p-6 rounded-2xl border border-white/8">
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-bold mb-3">Privacy notice</p>
              <p className="text-xs text-white/50 font-light leading-relaxed">Your information is used solely to respond to your enquiry. We never sell contact details and never send unsolicited marketing.</p>
            </div>
          </aside>

          <div className="lg:col-span-8">
            <div className="rounded-2xl border border-white/10 bg-[#0B1221]/80 backdrop-blur p-8 lg:p-12">
              {submitted ? (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 mx-auto mb-6 flex items-center justify-center">
                    <Check className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-display text-3xl text-white font-medium tracking-tight">Thanks — we&rsquo;ll be in touch.</h3>
                  <p className="text-white/50 mt-3 font-light">Most enquiries get a reply within one business day.</p>
                </motion.div>
              ) : (
                <form onSubmit={submit} className="space-y-6" data-testid="contact-form" noValidate>
                  <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
                    <div>
                      <label htmlFor="name" className={FIELD_LABEL}>Full name *</label>
                      <input id="name" type="text" value={data.name} onChange={update('name')} placeholder="Your full name"
                        className={FIELD_INPUT} data-testid="contact-name-input" aria-invalid={!!errors.name} />
                      {errors.name && <p className="text-red-400 text-xs mt-2 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="email" className={FIELD_LABEL}>Email *</label>
                      <input id="email" type="email" value={data.email} onChange={update('email')} placeholder="you@company.com"
                        className={FIELD_INPUT} data-testid="contact-email-input" aria-invalid={!!errors.email} />
                      {errors.email && <p className="text-red-400 text-xs mt-2 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.email}</p>}
                    </div>
                    <div>
                      <label htmlFor="phone" className={FIELD_LABEL}>Phone *</label>
                      <input id="phone" type="tel" value={data.phone} onChange={update('phone')} placeholder="+1 (___) ___-____"
                        className={FIELD_INPUT} data-testid="contact-phone-input" aria-invalid={!!errors.phone} />
                      {errors.phone && <p className="text-red-400 text-xs mt-2 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.phone}</p>}
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="projectType" className={FIELD_LABEL}>Project type</label>
                      <select id="projectType" value={data.projectType} onChange={update('projectType')} className={FIELD_SELECT} data-testid="contact-project-type">
                        <option value="">Select project type…</option>
                        {contactPage.projectTypes.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="budget" className={FIELD_LABEL}>Estimated budget</label>
                      <select id="budget" value={data.budget} onChange={update('budget')} className={FIELD_SELECT} data-testid="contact-budget">
                        <option value="">Select budget…</option>
                        {contactPage.budgets.map((b) => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="timeline" className={FIELD_LABEL}>Desired timeline</label>
                      <select id="timeline" value={data.timeline} onChange={update('timeline')} className={FIELD_SELECT} data-testid="contact-timeline">
                        <option value="">Select timeline…</option>
                        {contactPage.timelines.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="message" className={FIELD_LABEL}>Project brief *</label>
                    <textarea id="message" rows={5} value={data.message} onChange={update('message')} placeholder="Tell us a bit about the project — what you&rsquo;re building, who it&rsquo;s for, and what success looks like."
                      className={`${FIELD_INPUT} resize-none`} data-testid="contact-message-input" aria-invalid={!!errors.message} />
                    {errors.message && <p className="text-red-400 text-xs mt-2 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.message}</p>}
                  </div>
                  {globalErr && <p className="text-red-400 text-sm" data-testid="contact-error">{globalErr}</p>}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-white/5">
                    <p className="text-xs text-white/40 font-light">By submitting you agree to our privacy notice. We never spam.</p>
                    <button type="submit" disabled={submitting}
                      className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white text-[#050914] font-semibold hover:scale-[1.03] active:scale-[0.97] transition-transform duration-200 disabled:opacity-60"
                      data-testid="contact-submit-button">
                      {submitting ? 'Sending…' : 'Send Enquiry'}
                      {!submitting && <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-20 lg:py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <p className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-white/50 font-bold mb-6">
            <span className="w-8 h-px bg-white/30" /> Questions
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tighter text-white leading-[1.05] mb-10">
            Before you get in touch.
          </h2>
          <div>
            {faqs.map((f) => (
              <FAQRow key={f.id} faq={f} isOpen={openFaq === f.id} onToggle={() => setOpenFaq(openFaq === f.id ? null : f.id)} />
            ))}
            <div className="border-t border-white/8" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
